import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { RouterPath } from "../../../assets/dictionary/RouterPath";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Mail, CheckCircle, ArrowLeft } from "lucide-react";
import ConfirmEmailService from "../confirm_email/ConfirmEmailService";

export default function ConfirmationEmailSentModern() {
  const location = useLocation();
  const initialEmail = location.state?.email || "";
  const [email] = useState(initialEmail);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [resendError, setResendError] = useState("");

  const handleResend = async () => {
    if (!email) {
      return;
    }
    setIsResending(true);
    setResendMessage("");
    setResendError("");
    try {
      const response = await ConfirmEmailService.postResendConfirmEmail(email);
      if (response.status === 200) {
        setResendMessage("Un nouvel email de confirmation a été envoyé.");
      } else {
        setResendError("Impossible d'envoyer l'email de confirmation. Réessayez plus tard.");
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 404) {
        setResendError("Aucun compte n'a été trouvé avec cet email.");
      } else if (status === 400 && detail === "User mail is already confirmed") {
        setResendError("Votre email est déjà confirmé. Vous pouvez vous connecter.");
      } else if (status === 429) {
        setResendError("Un email vient déjà d'être envoyé. Merci d'attendre quelques minutes avant de réessayer.");
      } else {
        setResendError("Impossible d'envoyer l'email de confirmation. Réessayez plus tard.");
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 app-page-gradient">
      <div className="w-full max-w-md">
        <Card className="glass-card border-muted shadow-2xl">
          <CardHeader className="text-center space-y-4">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            
            <CardTitle className="text-2xl font-bold text-high-contrast">
              Email de confirmation envoyé !
            </CardTitle>
            
            <CardDescription className="text-muted-foreground">
              {email
                ? `Nous avons envoyé un email de confirmation à l'adresse ${email}. Veuillez vérifier votre boîte de réception et cliquer sur le lien de confirmation.`
                : "Nous avons envoyé un email de confirmation à votre adresse email. Veuillez vérifier votre boîte de réception et cliquer sur le lien de confirmation."}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <p className="font-medium mb-1">Vérifiez votre email</p>
                  <p>
                    Si vous ne voyez pas l'email, vérifiez votre dossier spam ou 
                    attendez quelques minutes avant de réessayer.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <Button asChild className="w-full">
                <Link to={RouterPath.LOGIN}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Retour à la connexion
                </Link>
              </Button>
              
              <div className="space-y-2">
                {email ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleResend}
                    disabled={isResending}
                  >
                    {isResending ? "Renvoi en cours..." : "Renvoyer l'email de confirmation"}
                  </Button>
                ) : (
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">
                      Vous n'avez pas reçu l'email ?{" "}
                      <Link 
                        to={RouterPath.SIGNUP}
                        className="text-primary hover:underline font-medium"
                      >
                        Réessayer
                      </Link>
                    </p>
                  </div>
                )}
                {resendMessage && (
                  <p className="text-sm text-green-600 text-center">{resendMessage}</p>
                )}
                {resendError && (
                  <p className="text-sm text-red-600 text-center">{resendError}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 